import React from "react";
import { CheckCircleFilled } from "@ant-design/icons";
import { Typography, Tooltip } from "antd";
import {
  WarningOutlined,
  StopOutlined,
  MedicineBoxOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { getFullName } from "../../../components/util/helpers";
import moment from "moment";
import { useIntl } from "react-intl";

const { Text } = Typography;
const CustomEvent = (props) => {
  const { event, patientsPallette, eventProps } = props;
  const { slotStart } = eventProps;
  const intl = useIntl();
  const order_id =
    event["order"] && event["order"]["id"] ? event["order"]["id"] : null;
  const patientColor = order_id
    ? patientsPallette.filter((item) => item.orderId === order_id)[0]
    : null;
  const color = patientColor
    ? patientColor.primary
    : event.nonAvailable
    ? "#ffffff"
    : "initial";
  const fullName =
    event && event.order
      ? getFullName(
          event.order.patient.surename,
          event.order.patient.first_name
        )
      : "";
  const isMonthView = slotStart !== undefined;
  moment.relativeTimeRounding((t) => {
    const DIGITS = 2; // like: 2.56 minutes
    return Math.round(t * Math.pow(10, DIGITS)) / Math.pow(10, DIGITS);
  });
  moment.relativeTimeThreshold("y", 365);
  moment.relativeTimeThreshold("M", 12);
  moment.relativeTimeThreshold("w", 4);
  moment.relativeTimeThreshold("d", 31);
  moment.relativeTimeThreshold("h", 24);
  moment.relativeTimeThreshold("m", 60);
  moment.relativeTimeThreshold("s", 60);
  moment.relativeTimeThreshold("ss", 0);
  const diff = moment
    .duration(moment(event.end).diff(moment(event.start)))
    .asSeconds();

  const humanizeTimeFrame = moment.duration(diff, "s").humanize();

  const getEventTimeText = (event, color) => {
    if (event?.ok2go_info?.id) {
      const startTime = event?.ok2go_info?.date
        ? moment(event.ok2go_info.date).format("HH:mm")
        : "?";
      const endTime = event?.ok2go_info?.exit_date
        ? moment(event.ok2go_info.exit_date).format("HH:mm")
        : "?";
      return `${startTime} - ${endTime}`;
    } else {
      const startTime = moment(event.start).format("HH:mm");
      const endTime = moment(event.end).format("HH:mm");
      return `${startTime} - ${endTime}`;
    }
  };
  return event.holiday ? (
    <Text ellipsis={true} style={{ color: "#ffffff" }}>
      {event.title}
    </Text>
  ) : event.absence ? (
    <AbsenceDisplay event={event} />
  ) : !isMonthView ? (
    <Tooltip title={humanizeTimeFrame} key={"range"}>
      <div style={{ display: "flex", alignItems: "center" }}>
        {event.nonAvailable ? null : event.approved ? (
          <CheckCircleFilled style={{ color: color }} />
        ) : (
          <div
            style={{
              width: "6px",
              height: "6px",
              backgroundColor: color,
              borderRadius: "3px",
              display: "table",
            }}
          />
        )}
        {event.nonAvailable ? (
          <>
            <Text
              style={{
                color: color,
                padding: "0 5px",
                fontSize: "12px",
                fontWeight: "700",
              }}
            >
              {`${moment(event.start).format("HH:mm")} - ${moment(
                event.end
              ).format("HH:mm")}`}
            </Text>
            <Tooltip
              title={intl.formatMessage({
                id: "app.scheduling.employeeNonAvailableWarning",
              })}
              color={"red"}
              key={"warning"}
              placement="bottom"
            >
              <WarningOutlined style={{ padding: "0 5px", color: "red" }} />
            </Tooltip>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              padding: "0 5px",
            }}
          >
            <Text
              style={{
                color: color,
                textAlign: "start",
                fontSize: "12px",
                fontWeight: "700",
              }}
            >
              {`${moment(event.start).format("HH:mm")} - ${moment(
                event.end
              ).format("HH:mm")}`}
            </Text>
            <Text
              ellipsis={true}
              style={{ color: "initial", textAlign: "start" }}
            >
              {fullName}
            </Text>
            <Text
              ellipsis={true}
              style={{
                color: "initial",
                fontWeight: "700",
                fontSize: "12px",
                textAlign: "start",
              }}
            >
              {humanizeTimeFrame}
            </Text>
          </div>
        )}
      </div>
    </Tooltip>
  ) : (
    <Tooltip title={humanizeTimeFrame} key={"range"}>
      <div style={{ display: "flex", alignItems: "center" }}>
        {event.nonAvailable ? null : event.approved ? (
          <CheckCircleFilled style={{ color: color }} />
        ) : event.hospitalized ? (
          <MedicineBoxOutlined style={{ color: color }} />
        ) : (
          <div
            style={{
              width: "6px",
              height: "6px",
              backgroundColor: color,
              borderRadius: "3px",
              display: "table",
            }}
          />
        )}
        <Text
          style={{
            color: color,
            padding: "0 5px",
            fontSize: "12px",
            fontWeight: "700",
          }}
        >
          {getEventTimeText(event, color)}
        </Text>
        {event.nonAvailable ? (
          <Tooltip
            title={`${intl.formatMessage({
              id: "app.scheduling.employeeNonAvailableWarning",
            })}${
              event.place && event.place.trim() !== ""
                ? ` (${event.place})`
                : ""
            }`}
            color={"red"}
            key={"warning"}
            placement="bottom"
          >
            <WarningOutlined style={{ padding: "0 5px", color: "red" }} />
          </Tooltip>
        ) : (
          <Text ellipsis={true} style={{ color: "initial" }}>
            {fullName}
          </Text>
        )}
      </div>
    </Tooltip>
  );
};

export default CustomEvent;

const AbsenceDisplay = ({ event }) => {
  if (!event.absence) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        position: "relative",
        top: "50%",
      }}
    >
      <StopOutlined
        style={{ color: event.type === 4 ? "#000000" : "#8000ff" }}
      />
      <Text
        ellipsis={true}
        style={{
          color: event.type === 4 ? "#000000" : "#8000ff",
          padding: "0 4px",
        }}
      >
        {event.title}
      </Text>
    </div>
  );
};
